
import { useState, useMemo, useEffect } from "react";
import type { FBPost, IGPost, GA4Data } from "@/lib/types";
import { useSharedFilters } from "./useSharedFilters";
import { useAuth } from "@/lib/auth-context";
import { getAllowedAccounts } from "@/lib/restaurant-groups";
import { useGroups } from "./useGroups";
import { apiClient } from "@/lib/api-client";

function matchesPostType(filter: string, fbType: string | null, igMediaType: string | null, igProductType: string | null): boolean {
  if (filter === "all") return true;
  return fbType === filter || igMediaType === filter || igProductType === filter;
}

// --- Facebook ---

export function useFilteredFB() {
  const [rawData, setRawData] = useState<FBPost[]>([]);
  const [filters, setFilters] = useSharedFilters();
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { loaded: groupsLoaded } = useGroups();

  useEffect(() => {
    apiClient.get<FBPost[]>("/sheets/facebook")
      .then((data) => { setRawData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Filter by user access
  const allData = useMemo(() => {
    if (!user || !groupsLoaded) return [];
    if (user.groupIds.includes("all")) return rawData;
    const allowed = getAllowedAccounts(user.groupIds);
    return rawData.filter((p) => allowed.fb.includes(p.account_name));
  }, [rawData, user, groupsLoaded]);

  const accounts = useMemo(() => {
    return [...new Set(allData.map((p) => p.account_name).filter(Boolean))].sort();
  }, [allData]);

  const postTypes = useMemo(() => {
    return [...new Set(allData.map((p) => p.type).filter(Boolean))].sort() as string[];
  }, [allData]);

  const filtered = useMemo(() => {
    return allData.filter((p) => {
      if (p.date < filters.dateFrom || p.date > filters.dateTo) return false;
      if (filters.accountName !== "all" && p.account_name !== filters.accountName) return false;
      if (!matchesPostType(filters.postType, p.type, null, null)) return false;
      return true;
    });
  }, [allData, filters]);

  return { data: filtered, allData, accounts, postTypes, filters, setFilters, loading: loading || !groupsLoaded };
}

// --- Instagram ---

export function useFilteredIG() {
  const [rawData, setRawData] = useState<IGPost[]>([]);
  const [filters, setFilters] = useSharedFilters();
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { loaded: groupsLoaded } = useGroups();

  useEffect(() => {
    apiClient.get<IGPost[]>("/sheets/instagram")
      .then((data) => { setRawData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const allData = useMemo(() => {
    if (!user || !groupsLoaded) return [];
    if (user.groupIds.includes("all")) return rawData;
    const allowed = getAllowedAccounts(user.groupIds);
    return rawData.filter((p) => allowed.ig.includes(p.account_name));
  }, [rawData, user, groupsLoaded]);

  const accounts = useMemo(() => {
    return [...new Set(allData.map((p) => p.account_name).filter(Boolean))].sort();
  }, [allData]);

  const postTypes = useMemo(() => {
    const types = new Set<string>();
    allData.forEach((p) => {
      if (p.media_type) types.add(p.media_type);
      if (p.media_product_type) types.add(p.media_product_type);
    });
    return [...types].sort();
  }, [allData]);

  const filtered = useMemo(() => {
    return allData.filter((p) => {
      if (p.date < filters.dateFrom || p.date > filters.dateTo) return false;
      if (filters.accountName !== "all" && p.account_name !== filters.accountName) return false;
      if (!matchesPostType(filters.postType, null, p.media_type, p.media_product_type)) return false;
      return true;
    });
  }, [allData, filters]);

  return { data: filtered, allData, accounts, postTypes, filters, setFilters, loading: loading || !groupsLoaded };
}

// --- GA4 ---

export function useFilteredGA4() {
  const [rawData, setRawData] = useState<GA4Data[]>([]);
  const [filters, setFilters] = useSharedFilters();
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { loaded: groupsLoaded } = useGroups();

  useEffect(() => {
    apiClient.get<GA4Data[]>("/sheets/ga4")
      .then((data) => { setRawData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const allData = useMemo(() => {
    if (!user || !groupsLoaded) return [];
    if (user.groupIds.includes("all")) return rawData;
    const allowed = getAllowedAccounts(user.groupIds);
    return rawData.filter((d) => allowed.ga4.includes(d.account_name));
  }, [rawData, user, groupsLoaded]);

  const accounts = useMemo(() => {
    return [...new Set(allData.map((d) => d.account_name).filter(Boolean))].sort();
  }, [allData]);

  const filtered = useMemo(() => {
    return allData.filter((d) => {
      if (d.date < filters.dateFrom || d.date > filters.dateTo) return false;
      if (filters.accountName !== "all" && d.account_name !== filters.accountName) return false;
      return true;
    });
  }, [allData, filters]);

  return { data: filtered, allData, accounts, filters, setFilters, loading: loading || !groupsLoaded };
}

// --- Combined ---

export function useFilteredAll() {
  const fb = useFilteredFB();
  const ig = useFilteredIG();
  const ga4 = useFilteredGA4();

  const allAccounts = useMemo(() => {
    return [...new Set([...fb.accounts, ...ig.accounts, ...ga4.accounts])].sort();
  }, [fb.accounts, ig.accounts, ga4.accounts]);

  return {
    fb: fb.data,
    ig: ig.data,
    ga4: ga4.data,
    allFb: fb.allData,
    allIg: ig.allData,
    accounts: allAccounts,
    filters: fb.filters,
    setFilters: fb.setFilters,
    loading: fb.loading || ig.loading || ga4.loading,
  };
}
